import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import {
  PaymentChartProps,
  PieData,
} from "@/types/pos/payment-reports";
import { capitalizeFirstLetter } from "@/lib/helper";

const formatChartTooltip = (value: number) => {
  return Number(value).toLocaleString();
};

const CustomLegend = ({ data }: { data: PieData[] }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex flex-col gap-2 h-full max-h-[300px] custom-scrollbar overflow-y-auto pr-2 mt-4">
      {data.map((item, index) => {
        const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
        return (
          <div key={index} className="flex items-center gap-2 text-sm shrink-0">
            <div
              className="w-4 h-4 rounded shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{capitalizeFirstLetter(item.name)}</div>
              <div className="text-xs text-muted-foreground">
                {formatChartTooltip(item.value)} ({percent}%)
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
];

const STATUS_COLORS: Record<string, string> = {
  Paid: "#10b981", // Emerald-500
  Processing: "#f59e0b", // Amber-500
  Pending: "#0ea5e9", // Sky-500
  Failed: "#ef4444", // Red-500
  Cancelled: "#6b7280", // Gray-500
};

export default function PaymentChart({ data, isLoading }: PaymentChartProps) {
  const methodData = React.useMemo(() => {
    const methods: Record<string, number> = {};
    data.forEach((item) => {
      const method = item.payment_method || "Unknown";
      methods[method] = (methods[method] || 0) + Number(item.total_amount);
    });
    return Object.entries(methods).map(([name, value], index) => ({
      name,
      value,
      color: COLORS[index % COLORS.length],
    }));
  }, [data]);

  const statusData = React.useMemo(() => {
    const statuses: Record<string, number> = {};
    data.forEach((item) => {
      const status = item.status || "Unknown";
      statuses[status] = (statuses[status] || 0) + Number(item.total_amount);
    });
    return Object.entries(statuses).map(([name, value]) => ({
      name,
      value,
      color: STATUS_COLORS[name] || "#CBD5E1",
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className="w-full h-80 flex items-center justify-center bg-white rounded-lg border">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-80 flex items-center justify-center bg-white rounded-lg border">
        <p className="text-gray-500">No data available for the selected filters</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-semibold text-sm">{capitalizeFirstLetter(payload[0].name)}</p>
          <p className="text-primary text-sm">
            Amount: {Number(payload[0].value).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Payment Method Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
          Payment Method Distribution
        </h3>
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="w-full md:w-1/2 h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={methodData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {methodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full md:w-1/2">
            <CustomLegend data={methodData} />
          </div>
        </div>
      </div>

      {/* Status Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
          Payment Status Breakdown
        </h3>
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="w-full md:w-1/2 h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full md:w-1/2">
            <CustomLegend data={statusData} />
          </div>
        </div>
      </div>
    </div>
  );
}


